import { Button, Dialog } from "@chotca/ui";
import { LEGAL_COMPANY as C } from "@chotca/config";

// LEGAL-04 — Màn xác nhận đơn hàng trước khi thanh toán.
//
// Luật Bảo vệ quyền lợi người tiêu dùng 19/2023/QH15 buộc phải hiển thị đầy đủ
// điều kiện giao dịch (Đ.37.1) VÀ có bước xác nhận đồng ý giao kết (Đ.38.1.b)
// TRƯỚC khi tạo đơn. Thiếu bước này, khách có quyền huỷ ngang 30 ngày (Đ.38.3.b).
//
// UI: tóm tắt đơn hàng hiển thị luôn; 8 nhóm điều kiện giao dịch (Đ.37.1) gập
// trong accordion "Xem điều khoản giao dịch" — vẫn xem được TẠI CHỖ (không rời
// trang), nên thoả Đ.37.1 mà không làm dialog quá dài.
//
// Mọi thông tin pháp nhân đọc từ LEGAL_COMPANY — không gõ tay, đổi một chỗ.

// Marketing site serves legal docs at chotca.com/<slug> (apps/web (legal)/[slug]).
const PAYMENT_TERMS_URL = `${C.websiteUrl}/payment-terms`;

export type OrderConfirmDetails = {
  productName: string; // tên gói / "Mua thêm N lượt săn"
  featureLines: string[]; // số tin, lượt săn, lượt boost…
  amountVnd: number;
  priceSuffix?: string; // "/ tháng" cho gói, rỗng cho lượt
};

function fmtVnd(n: number): string {
  return `${n.toLocaleString("vi-VN")}₫`;
}

// One key/value row. Label never wraps; value right-aligns.
function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 text-sm text-ash">{label}</span>
      <span className="text-right text-sm font-semibold text-ink">
        {children}
      </span>
    </div>
  );
}

// One legal group inside the accordion (Đ.37.1).
function TermsGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-smoke pt-2.5 first:border-t-0 first:pt-0">
      <p className="text-xs font-bold uppercase tracking-wide text-ash">
        {title}
      </p>
      <div className="mt-1 space-y-0.5 text-xs text-ink">{children}</div>
    </div>
  );
}

export function OrderConfirmDialog({
  open,
  order,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  order: OrderConfirmDetails | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open && !!order} onClose={onClose} title="Xác nhận đơn hàng">
      {order ? (
        <div className="space-y-4">
          {/* Order summary. */}
          <div className="rounded-2xl border border-smoke bg-paper p-4">
            <p className="text-base font-bold text-ink">{order.productName}</p>
            <ul className="mt-1 space-y-0.5 text-sm text-ink">
              {order.featureLines.map((line) => (
                <li key={line}>• {line}</li>
              ))}
            </ul>

            <div className="mt-3 border-t border-smoke pt-2">
              <Row label="Thời hạn">30 ngày</Row>
              <Row label="Thanh toán">Chuyển khoản QR</Row>
            </div>

            <div className="mt-2 flex items-baseline justify-between border-t border-smoke pt-3">
              <span className="text-sm font-bold text-ink">Tổng cộng</span>
              <span className="text-lg font-extrabold text-ink">
                {fmtVnd(order.amountVnd)}
                {order.priceSuffix ? (
                  <span className="text-sm font-normal text-ash">
                    {" "}
                    {order.priceSuffix}
                  </span>
                ) : null}
              </span>
            </div>
            <p className="mt-1 text-right text-xs text-ash">
              Đã bao gồm thuế GTGT · Không tự động gia hạn
            </p>
          </div>

          {/* Full transaction terms (Đ.37.1) — collapsed, viewable in place. */}
          <details className="rounded-2xl border border-smoke">
            <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-ink">
              Xem điều khoản giao dịch
            </summary>
            <div className="space-y-2.5 px-4 pb-4">
              <TermsGroup title="Bên bán">
                <p className="font-semibold">{C.legalName}</p>
                {C.taxId ? <p>Mã số thuế: {C.taxId}</p> : null}
                <p>{C.address}</p>
                <p>Email liên hệ: {C.email}</p>
              </TermsGroup>

              <TermsGroup title="Dịch vụ">
                <p className="font-semibold">{order.productName}</p>
                {order.featureLines.map((line) => (
                  <p key={line}>• {line}</p>
                ))}
                <p>• Thời hạn sử dụng: 30 ngày kể từ khi kích hoạt</p>
              </TermsGroup>

              <TermsGroup title="Giá">
                <p>
                  {fmtVnd(order.amountVnd)}
                  {order.priceSuffix ? ` ${order.priceSuffix}` : ""} — đã bao
                  gồm thuế GTGT.
                </p>
              </TermsGroup>

              <TermsGroup title="Phương thức thanh toán">
                <p>Chuyển khoản ngân hàng bằng mã QR (VietQR).</p>
                <p>
                  Đơn hàng có hiệu lực thanh toán trong 30 phút kể từ khi tạo.
                  Quá hạn, đơn tự huỷ và bạn có thể tạo đơn mới.
                </p>
              </TermsGroup>

              <TermsGroup title="Chính sách đổi trả">
                <p>
                  Gói đã kích hoạt không hoàn tiền, trừ khi lỗi thuộc về{" "}
                  {C.brandName}.
                </p>
                <p>
                  Lượt săn ứng viên được hoàn tự động nếu người tìm việc từ chối
                  hoặc để hết hạn 24 giờ.
                </p>
              </TermsGroup>

              <TermsGroup title="Gia hạn">
                <p>
                  Gói không tự động gia hạn và hệ thống không tự động trừ tiền.
                  Hết hạn, tài khoản trở về gói Miễn phí.
                </p>
              </TermsGroup>

              <TermsGroup title="Khiếu nại">
                <p>Email tiếp nhận: {C.billingEmail}</p>
                <p>Thời hạn phản hồi cam kết: trong vòng 3 ngày làm việc.</p>
              </TermsGroup>

              <TermsGroup title="Quyền của người mua">
                <p>
                  Nếu thông tin cung cấp không chính xác hoặc không đầy đủ, bạn
                  có quyền đơn phương chấm dứt hợp đồng trong 30 ngày và không
                  phải trả chi phí ngoài phần dịch vụ đã sử dụng (Điều 38 khoản
                  3).
                </p>
              </TermsGroup>
            </div>
          </details>

          {/* Đồng ý giao kết — Đ.38.1.b: bấm "Thanh toán" là hành động chủ động
              xác nhận đồng ý, nêu rõ ngay trên nút (không dùng checkbox). */}
          <p className="text-xs text-ash">
            Bấm “Thanh toán” là bạn đồng ý{" "}
            <a
              href={PAYMENT_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-lime-dark underline"
            >
              Điều khoản thanh toán
            </a>{" "}
            và đặt mua đơn hàng này.
          </p>

          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={onClose}>
              Huỷ
            </Button>
            <Button className="flex-1" loading={loading} onClick={onConfirm}>
              Thanh toán
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
